import React, { useEffect, useRef } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import {
  GEOCODING_COUNTRY,
  GOOGLE_MAPS_LIBRARIES,
} from "../../pages/canchas/components/mapConfig";

// Address field for the organizer club form, enhanced with Google Places
// Autocomplete.
//
// Uses the legacy `places.Autocomplete` widget rather than the newer
// PlaceAutocompleteElement on purpose: it attaches to our own <input>, so the
// field keeps the `zr-input` styling shared by every other field in the form.
// PlaceAutocompleteElement renders its own shadow-DOM input, which would look
// foreign here.
//
// Picking a suggestion is an explicit human choice, so it commits coordinates
// straight away — same contract as dragging the pin on the map.

function extractStreetAddress(place) {
  const components = place?.address_components || [];
  const findComponent = (type) =>
    components.find((component) => component.types?.includes(type))?.long_name || "";

  const route = findComponent("route");
  const streetNumber = findComponent("street_number");

  // Keep only the street portion: city and province are separate fields in
  // this form, and repeating them here would duplicate them in every address
  // string built downstream.
  if (route) return [route, streetNumber].filter(Boolean).join(" ");

  return place?.name || place?.formatted_address || "";
}

export default function ClubAddressAutocomplete({ value, onChange, onPlaceSelected }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Keep the latest callback in a ref so re-creating the widget is not tied to
  // the identity of the handler passed by the parent.
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  useEffect(() => {
    onPlaceSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  useEffect(() => {
    if (!isLoaded || !inputRef.current) return undefined;
    if (!window.google?.maps?.places?.Autocomplete) return undefined;
    if (autocompleteRef.current) return undefined;

    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: GEOCODING_COUNTRY },
      fields: ["address_components", "geometry", "name", "formatted_address"],
      types: ["address"],
    });
    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      const location = place?.geometry?.location;
      if (!location) return;

      onPlaceSelectedRef.current?.({
        address: extractStreetAddress(place),
        lat: location.lat(),
        lng: location.lng(),
      });
    });

    return () => {
      listener?.remove?.();
      autocompleteRef.current = null;
    };
  }, [isLoaded]);

  return (
    <input
      ref={inputRef}
      className="zr-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Av. Libertador 100"
      // The widget renders its dropdown in a detached container; browser
      // autofill would overlap it.
      autoComplete="off"
      onKeyDown={(e) => {
        // Enter on a suggestion must pick the place, never submit the form.
        if (e.key === "Enter") e.preventDefault();
      }}
    />
  );
}
